import PropTypes from 'prop-types';
import { PUNCTUATION } from '../../../../utils/constants/notation';
import IconButton, { ICON_BUTTON_ICONS, ICON_BUTTON_TYPES } from '../../../buttons/IconButton';
import { FIELD_SOURCE, dataSourceClass } from '../../../../utils/constants/dataSources';
import { openInNewTabButtonProps } from '../../../../utils/openInNewTab';
import { canNavigateToNodeDetails, formatShare, locationPartsOf } from './graph/graphCardModel';
import LocationText from './LocationText';
import { linkedRowProps, rowKeyOf } from './linkedRowProps';
import ShowInGraphButton from './ShowInGraphButton';
import { GRAPH_COPY } from './graph/graphConstants';

// Rows are the graph's subsidiary nodes. Gate on the same check the new-tab handler
// (onOpenNodeInNewTab) applies, so an enabled icon never offers a new tab that does nothing.
const canViewDetails = (entry) => canNavigateToNodeDetails(entry);

/**
 * Get tooltip text based on navigability
 */
const getTooltipText = (entry) => {
  return canViewDetails(entry) ? 'View company details' : 'Details not available';
};

// Shares and locations read as on the subsidiary cards of the graph; the location on two lines
// when it does not fit on one (LocationText).
const rowLocation = (entry) => {
  const parts = locationPartsOf(entry.city, entry.country_name);
  return parts.length ? <LocationText parts={parts} /> : PUNCTUATION.EMPTY_VALUE;
};

// Without isInGraph every row counts as drawn.
const everyRow = () => true;

/**
 * SubsidiariesTable - Displays list of subsidiaries
 *
 * @param {Object} props
 * @param {Array} props.subsidiaries - Array of subsidiary entries
 * @param {boolean} props.isPhoneOrTablet - Device type for styling
 * @param {string} props.layout - Layout mode ('desktop', 'tablet', 'mobile')
 * @param {number} props.cardHeight - Parent card height for dynamic sizing
 * @param {Function} props.onViewDetails - Callback when view details is clicked (TD-1709)
 * @param {Function} [props.onOpenDetailsInNewTab] - Opens the entry's details in a new tab
 *   (Cmd/Ctrl-click and the right-click menu on the view-details icon)
 * @param {Function} [props.onHoverSubsidiary] - (entry | null) => the row under the pointer or
 *   holding the focus, for the graph above to highlight that subsidiary; with it the rows show a
 *   hover state
 * @param {Function} [props.isInGraph] - (entry) => whether the graph draws the row's card; only
 *   those rows are linked (default: every row)
 * @param {Function} [props.onShowInGraph] - (entry) => reveals a card the graph does not draw;
 *   with it such rows offer 'Show in graph'
 */
const SubsidiariesTable = ({
  subsidiaries = [],
  isPhoneOrTablet = false,
  layout = 'desktop',
  cardHeight = 0,
  onViewDetails,
  onOpenDetailsInNewTab,
  onHoverSubsidiary,
  isInGraph = everyRow,
  onShowInGraph
}) => {
  /**
   * CALCULATION OWNERSHIP: This component does NOT calculate - uses value directly
   * - Receives: cardHeight (already calculated as (measuredHeight - 20) / 2 by parent)
   * - Uses: cardHeight directly without modification
   * - DO NOT apply division or calculation here - parent owns the calculation
   */
  const dynamicStyle =
    layout === 'desktop' && cardHeight > 0
      ? {
          minHeight: `${cardHeight}px`,
          maxHeight: `${cardHeight}px`
        }
      : {};

  return (
    <div className="card" style={dynamicStyle}>
      <div className={`heading ${dataSourceClass(FIELD_SOURCE.subsidiaries)}`}>Subsidiaries</div>
      <div className="subsidiaries-header">
        <div className="item name">Company name</div>
        <div className="item location">City, Country</div>
        <div className="item direct-ownership">Direct Ownership</div>
        <div className="item total-ownership">Total Ownership</div>
        {onViewDetails && <div className="item actions"></div>}
      </div>
      {!!subsidiaries?.length && (
        <div className={`entities-list${isPhoneOrTablet ? '' : ' scrollbar-styles'}`}>
          {subsidiaries.map((entry, index) => {
            const inGraph = isInGraph(entry);
            const onHoverRow = inGraph ? onHoverSubsidiary : undefined;
            return (
              <div
                className={`subsidiaries-row${onHoverRow ? ' subsidiaries-row--linked' : ''}`}
                key={rowKeyOf(entry, index)}
                {...linkedRowProps(entry, onHoverRow)}
              >
                <div className="item name">{entry.member_name}</div>
                <div className="item location">{rowLocation(entry)}</div>
                <div className="item direct-ownership">{formatShare(entry.direct_ownership)}</div>
                <div className="item total-ownership">{formatShare(entry.total_ownership)}</div>
                {onViewDetails && (
                  <div className="item actions">
                    {!inGraph && onShowInGraph && (
                      <ShowInGraphButton
                        entry={entry}
                        onShow={onShowInGraph}
                        hint={GRAPH_COPY.SHOW_SUBSIDIARY_IN_GRAPH_HINT}
                      />
                    )}
                    <IconButton
                      wrapClassName="view-details-btn"
                      icon={ICON_BUTTON_ICONS.VIEW_DETAILS_CUSTOM1}
                      type={ICON_BUTTON_TYPES.BLUE}
                      {...openInNewTabButtonProps({
                        activate: () => onViewDetails(entry),
                        openInNewTab: onOpenDetailsInNewTab && (() => onOpenDetailsInNewTab(entry)),
                        enabled: canViewDetails(entry)
                      })}
                      tooltipText={getTooltipText(entry)}
                      disabled={!canViewDetails(entry)}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

SubsidiariesTable.propTypes = {
  subsidiaries: PropTypes.array,
  isPhoneOrTablet: PropTypes.bool,
  layout: PropTypes.string,
  cardHeight: PropTypes.number,
  onViewDetails: PropTypes.func,
  onOpenDetailsInNewTab: PropTypes.func,
  onHoverSubsidiary: PropTypes.func,
  isInGraph: PropTypes.func,
  onShowInGraph: PropTypes.func
};

export default SubsidiariesTable;
